'use client';

import React, { createContext, useContext, useMemo } from 'react';
import { useSession } from '@/app/SessionProvider';

type Ctx = {
  effectiveUserId: string;  // whose data to read
  readOnly: boolean;        // true when impersonating someone else
  isAdmin: boolean;         // platform admin flag
};

export const EffectiveUserContext = createContext<Ctx | null>(null);

type Props = {
  children: React.ReactNode;
  effectiveUserIdFromServer: string | null;
  isAdmin: boolean;
};

export function EffectiveUserProvider({
  children,
  effectiveUserIdFromServer,
  isAdmin,
}: Props) {
  const { user } = useSession();

  const value = useMemo<Ctx>(() => {
    const selfId = user?.id ?? '';
    const eff = effectiveUserIdFromServer ?? selfId;
    const readOnly = isAdmin && eff !== selfId;
    return { effectiveUserId: eff, readOnly, isAdmin };
  }, [user?.id, effectiveUserIdFromServer, isAdmin]);

  return (
    <EffectiveUserContext.Provider value={value}>
      {children}
    </EffectiveUserContext.Provider>
  );
}

export function useEffectiveUser() {
  const ctx = useContext(EffectiveUserContext);
  if (!ctx) throw new Error('useEffectiveUser must be used within EffectiveUserProvider');
  return ctx;
}
